import React, { useState, useRef } from 'react';
import DatePickerInput from '../form-elements/DatePickerInput';
import Select from '../form-elements/Select';
import ScheduleInput from '../form-elements/ScheduleInput';
import SalaryInput from '../form-elements/SalaryInput';
import { GENDER_OPTIONS } from '../../constants/formOptions';
import { validateField } from '../../utils/validateField';

// --- Кастомные стили и тема для react-select с использованием CSS-переменных Telegram ---
const getSelectTheme = (theme) => {
  const root = document.documentElement;
  const bg = getComputedStyle(root).getPropertyValue('--tg-theme-bg-color') || '#fff';
  const text = getComputedStyle(root).getPropertyValue('--tg-theme-text-color') || '#000';
  const button = getComputedStyle(root).getPropertyValue('--tg-theme-button-color') || '#2481cc';
  const buttonText = getComputedStyle(root).getPropertyValue('--tg-theme-button-text-color') || '#fff';
  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary25: button.trim(), // hover
      primary: button.trim(), // selected
      neutral0: bg.trim(), // control/menu bg
      neutral80: text.trim(), // text
      neutral20: 'rgba(153,153,153,0.3)', // border
      neutral30: button.trim(), // border on focus
      neutral60: '#aaa', // placeholder
      neutral90: text.trim(), // single value
    },
  };
};
const getSelectStyles = () => {
  const root = document.documentElement;
  const bg = getComputedStyle(root).getPropertyValue('--tg-theme-bg-color') || '#fff';
  const text = getComputedStyle(root).getPropertyValue('--tg-theme-text-color') || '#000';
  const button = getComputedStyle(root).getPropertyValue('--tg-theme-button-color') || '#2481cc';
  const buttonText = getComputedStyle(root).getPropertyValue('--tg-theme-button-text-color') || '#fff';
  return {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: bg.trim(),
      color: text.trim(),
      borderColor: state.isFocused ? button.trim() : 'rgba(153,153,153,0.3)',
      boxShadow: state.isFocused ? `0 0 0 1.5px ${button.trim()}` : undefined,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: bg.trim(),
      color: text.trim(),
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? button.trim()
        : state.isFocused
        ? 'rgba(36,129,204,0.15)'
        : bg.trim(),
      color: state.isSelected ? buttonText.trim() : text.trim(),
    }),
    singleValue: (provided) => ({
      ...provided,
      color: text.trim(),
    }),
    input: (provided) => ({
      ...provided,
      color: text.trim(),
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#aaa',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: text.trim(),
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(153,153,153,0.3)',
    }),
  };
};

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function scrollToField(ref) {
  if (ref && ref.current && isIOS()) {
    setTimeout(() => {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 200);
  }
}

function scrollToElementById(id) {
  const element = document.getElementById(id);
  if (element) {
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }, 100);
  }
}

const NannySearchForm = ({ 
  formData, 
  errors, 
  isSubmitting, 
  handleInputChange, 
  handleChildInfoChange,
  addChild,
  removeChild,
  handleSubmit,
  isSubmitted,
  fieldRefs
}) => {
  const [focusedField, setFocusedField] = useState(null);
  const [focusedChildField, setFocusedChildField] = useState(null);

  return (
    <form onSubmit={handleSubmit} className="form-container">
      <div className="form-header">
        <h2>Анкета для поиска няни</h2>
      </div>
      
      <div ref={fieldRefs?.location} className={`form-group${errors && errors.location ? ' error' : ''}`} style={{ position: 'relative' }}>
        <label className="required-field">Локация:</label>
        <input
          type="text"
          name="location"
          value={formData.location?.address || ''}
          onChange={(e) => handleInputChange({
            target: {
              name: 'location',
              value: { ...formData.location, address: e.target.value }
            }
          })}
          placeholder="Адрес или район"
          onFocus={() => { setFocusedField('location'); scrollToField(fieldRefs?.location); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'location' && formData.location?.address && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'location', value: { ...formData.location, address: '' } } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div ref={fieldRefs?.childrenInfo} className={`form-group${errors && errors.childrenInfo ? ' error' : ''}`}>
        <label className="required-field">Дети:</label>
        {(formData.childrenInfo || [{}]).map((child, index) => (
          <div key={index} className="child-info">
            {index > 0 && (
              <div className="child-info-header">
                <button 
                  type="button" 
                  onClick={() => removeChild(index)} 
                  className="remove-child-button"
                >
                  ✕
                </button>
              </div>
            )}
            <div style={{ position: 'relative' }}>
              <div style={{ width: '100%' }}>
                <input
                  type="text"
                  value={child.age || ''}
                  onChange={(e) => handleChildInfoChange(index, 'age', e.target.value)}
                  placeholder="Возраст (например, 7 месяцев, 1.5 года, 12 лет)"
                  style={{ width: '100%' }}
                  onFocus={() => { setFocusedChildField && setFocusedChildField(`child-age-${index}`); scrollToField(fieldRefs?.childrenInfo); }}
                  onBlur={() => setFocusedChildField && setFocusedChildField(null)}
                />
              </div>
            </div>
            <Select
              options={GENDER_OPTIONS}
              value={GENDER_OPTIONS.find(opt => opt.value === child.gender)}
              onChange={(option) => handleChildInfoChange(index, 'gender', option.value)}
              placeholder="Пол"
              isSearchable={false}
              isClearable={false}
              isReadOnly={true}
              onFocus={() => { setFocusedChildField && setFocusedChildField(`child-gender-${index}`); scrollToField(fieldRefs?.childrenInfo); }}
              onBlur={() => setFocusedChildField && setFocusedChildField(null)}
            />
            <div className="child-info-textarea-wrapper">
              <textarea
                className="child-info-textarea"
                value={child.additionalInfo || ''}
                onChange={(e) => handleChildInfoChange(index, 'additionalInfo', e.target.value)}
                placeholder="Дополнительная информация"
                onFocus={() => { setFocusedChildField && setFocusedChildField(`child-additionalInfo-${index}`); scrollToField(fieldRefs?.childrenInfo); }}
                onBlur={() => setFocusedChildField && setFocusedChildField(null)}
              />
              {typeof focusedChildField !== 'undefined' && focusedChildField === `child-additionalInfo-${index}` && child.additionalInfo && (
                <button
                  type="button"
                  className="child-info-textarea-clear"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => handleChildInfoChange(index, 'additionalInfo', '')}
                  tabIndex={-1}
                  aria-label="Очистить"
                >×</button>
              )}
            </div>
          </div>
        ))}
        <button type="button" onClick={addChild} className="add-child-button">
          Добавить ребенка
        </button>
      </div>

      <div ref={fieldRefs?.schedule} className={`form-group${errors && errors.schedule ? ' error' : ''}`}>
        <label className="required-field">График работы:</label>
        <ScheduleInput
          value={formData.schedule}
          onChange={handleInputChange}
          category="nanny-search"
          onFocus={() => { setFocusedField('schedule'); scrollToField(fieldRefs?.schedule); }}
          onBlur={() => setFocusedField(null)}
        />
      </div>

      <div ref={fieldRefs?.duties} className={`form-group${errors && errors.duties ? ' error' : ''}`} style={{ position: 'relative' }}>
        <label className="required-field">Обязанности:</label>
        <textarea
          name="duties"
          value={formData.duties || ''}
          onChange={handleInputChange}
          placeholder="Опишите основные обязанности"
          onFocus={() => { setFocusedField && setFocusedField('duties'); scrollToField(fieldRefs?.duties); }}
          onBlur={() => setFocusedField && setFocusedField(null)}
        />
        {typeof focusedField !== 'undefined' && focusedField === 'duties' && formData.duties && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'duties', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div className="form-group" style={{ position: 'relative' }}>
        <label>Пожелания к кандидату:</label>
        <textarea
          name="requirements"
          value={formData.requirements || ''}
          onChange={handleInputChange}
          placeholder="Дополнительные пожелания к кандидату"
          onFocus={() => { setFocusedField('requirements'); scrollToField(fieldRefs?.requirements); }}
          onBlur={() => setFocusedField(null)}
          className={validateField('requirements', formData.requirements, 'nanny-search') ? 'error' : ''}
        />
        {typeof focusedField !== 'undefined' && focusedField === 'requirements' && formData.requirements && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'requirements', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div className="form-group">
        <label>Дата начала работы:</label>
        <DatePickerInput
          value={formData.startDate}
          onChange={(e) => handleInputChange({
            target: { name: 'startDate', value: e.target.value }
          })}
          placeholder="Выберите дату"
          minDate={new Date()}
          onFocus={() => { setFocusedField('startDate'); scrollToField(fieldRefs?.startDate); }}
          onBlur={() => setFocusedField(null)}
        />
      </div>

      <div className="form-group">
        <label>Оплата:</label>
        <SalaryInput
          value={formData.salary}
          onChange={handleInputChange}
          onFocus={() => { setFocusedField('salary'); scrollToField(fieldRefs?.salary); }}
          onBlur={() => setFocusedField(null)}
        />
      </div>

      <div ref={fieldRefs?.fullName} className={`form-group${errors && errors.fullName ? ' error' : ''}`} style={{ position: 'relative' }}>
        <label className="required-field">Имя, Фамилия:</label>
        <input
          type="text"
          id="form-fullName"
          name="fullName"
          value={formData.fullName || ''}
          onChange={handleInputChange}
          placeholder="Ваше полное имя"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onFocus={() => { setFocusedField('fullName'); scrollToElementById('form-fullName'); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'fullName' && formData.fullName && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'fullName', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div ref={fieldRefs?.contacts} className={`form-group${errors && errors.contacts ? ' error' : ''}`} style={{ position: 'relative' }}>
        <label className="required-field">Контакты:</label>
        <input
          type="text"
          id="form-contacts"
          name="contacts"
          value={formData.contacts || ''}
          onChange={handleInputChange}
          placeholder="Телефон или другой способ связи"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onFocus={() => { setFocusedField('contacts'); scrollToElementById('form-contacts'); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'contacts' && formData.contacts && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'contacts', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <button 
        type="submit" 
        className={`submit-button ${isSubmitting ? 'loading' : ''}`}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Отправка...' : 'Отправить'}
      </button>
    </form>
  );
};

export default NannySearchForm; 